import { g as getElementDimensions } from './index-Cg3DbsRO.js';
import { d as debounce } from './debounce-Dg7s1ivP.js';

function throttle(func, throttleMs, { signal, edges = ['leading', 'trailing'] } = {}) {
    let pendingAt = null;
    const debounced = debounce(func, throttleMs, { signal, edges });
    const throttled = function (...args) {
        if (pendingAt == null) {
            pendingAt = Date.now();
        }
        else {
            if (Date.now() - pendingAt >= throttleMs) {
                pendingAt = Date.now();
                debounced.cancel();
            }
        }
        debounced.apply(this, args);
    };
    throttled.cancel = debounced.cancel;
    throttled.flush = debounced.flush;
    return throttled;
}

class HeaderElement extends HTMLElement {
  connectedCallback() {
    this.lastScrollPosition = window.scrollY;
    this.getHeaderHeight();
    this.handleScroll();
    this.updateHeaderTop();

    window.addEventListener('resize', throttle(this.getHeaderHeight, 200));
    window.addEventListener('scroll', throttle(this.handleScroll, 200));

    this.primaryNav = this.querySelector('[data-nav-primary]');

    // Add event listener to handle clicks on primary nav
    this.primaryNav.addEventListener('click', (event) => {
      this.handleNavLinkEvent(event);
    });

    // Open the dropdown when hovering over a dropdown-controller in primary nav
    this.primaryNav.addEventListener('mouseover', (event) => {
      if (document.documentElement.classList.contains('touch')) return;
      if (!event.target.closest('[data-dropdown-controller]')) return;
      this.handleNavDrawerEvent(event);
    });

    // Close the dropdown when hovering over any clickable element in the header
    this.addEventListener('mouseover', (event) => {
      if (document.documentElement.classList.contains('touch')) return;
      const clickableElement = event.target.closest('a, button, [role="button"]');
      if (!clickableElement || clickableElement.closest('dropdown-element')) return;
      this.handleNavLinkEvent(event);
    });

    // On page scroll, close any open dropdown
    window.addEventListener('scroll', () => {
      this.querySelector('[aria-hidden="false"]')?.close();
    });

    // Debounced - Open the dropdown
    this.debouncedMouseover = debounce(() => {
      this.openDrawer.open();
    }, 200);
  }

  /**
   * Close the open dropdown when handling a link that does not control a dropdown
   * @param {Event} event - The event object
   */
  handleNavLinkEvent(event) {
    const controller = event.target.closest('[data-dropdown-controller]');

    // If clicking on a dropdown controller, allow the link to work
    if (controller) return;

    // Close dropdowns when clicking on other elements
    if (!event.target.closest('dropdown-element')) {
      this.querySelector('[aria-hidden="false"]')?.close();
    }
  }

  /**
   * Handle dropdown events in primary nav, find the dropdown and if it exists - handle the action
   * @param {Event} event - The event object
   */
  handleNavDrawerEvent(event) {
    const controller = event.target.closest('[data-dropdown-controller]');
    const dropdownId = controller?.getAttribute('aria-controls');
    if (!dropdownId) return;

    this.openDrawer = document.getElementById(dropdownId);
    this.debouncedMouseover();
  }

  /**
   * Close all dropdowns
   */
  closeAllNavDrawers() {
    this.dropdowns = this.dropdowns || this.primaryNav.querySelectorAll('dropdown-element');

    // Close all dropdowns
    this.dropdowns.forEach((dropdown) => {
      dropdown.close();
    });
  }

  /**
   * Set header height in global scope and CSS var
   */
  getHeaderHeight = () => {
    const headerHeight = getElementDimensions('header-element').height;
    window.HEADER_HEIGHT = headerHeight;
    document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
  };

  /**
   * Set header top in global scope and CSS var
   */
  updateHeaderTop = (top = 0) => {
    const headerTop = `${-top}px`;
    document.documentElement.style.setProperty('--header-top', headerTop);
    document.documentElement.classList.toggle('header-visible', top === 0);
  };

  /**
   * Handle scroll event
   */
  handleScroll = () => {
    const currentScrollPosition = window.scrollY;

    // Don't animate if we're at the top of the page
    if (currentScrollPosition <= window.HEADER_HEIGHT) {
      this.updateHeaderTop();
      this.lastScrollPosition = currentScrollPosition;
      return;
    }

    // Hide header while actively scrolling
    if (currentScrollPosition !== this.lastScrollPosition) {
      this.updateHeaderTop(window.HEADER_HEIGHT);

      // Clear any existing timeout
      if (this.scrollTimeout) clearTimeout(this.scrollTimeout);

      // Set timeout to show header when scrolling stops
      this.scrollTimeout = setTimeout(() => this.updateHeaderTop(), 600);
    }

    this.lastScrollPosition = currentScrollPosition;
  };
}

customElements.define('header-element', HeaderElement);
